interface Resumo {
  total: number;
  importados: number;
  aguardando_empresa: number;
  duplicidades: number;
  pendentes: number;
  erros: number;
}

interface RegrasContagem {
  total: number;
  aplicadas: number;
  aguardando: number;
  ignoradas: number;
}

interface DetalheRegra {
  alvo: string;
  resultado: 'aplicada' | 'ambigua' | 'nao_encontrada';
  entidade_tipo?: string;
  entidade_id?: number;
  candidatos?: string[];
}

interface Props {
  batch: any;
  resumo: Resumo | null;
  regras: RegrasContagem | null;
  detalheRegras: DetalheRegra[] | null;
  onAplicarRegras: () => void;
  aplicandoRegras: boolean;
}

function Kpi({ label, value, tone }: { label: string; value: number | null | undefined; tone: string }) {
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <p className="text-xl font-bold">{value ?? 0}</p>
      <p className="text-[11px] font-semibold opacity-80">{label}</p>
    </div>
  );
}

export default function ImportacaoRelatorio({ batch, resumo, regras, detalheRegras, onAplicarRegras, aplicandoRegras }: Props) {
  const r = resumo;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center bg-green-50 text-green-600 rounded-lg">
          <i className="ri-file-chart-line"></i>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Relatório da importação</h3>
          {batch && <p className="text-xs text-gray-500">{batch.arquivo_nome} · {batch.status}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Kpi label="Registros lidos" value={r?.total} tone="bg-gray-50 border-gray-200 text-gray-700" />
        <Kpi label="Importados" value={r?.importados} tone="bg-green-50 border-green-200 text-green-700" />
        <Kpi label="Sem empresa (fila)" value={r?.aguardando_empresa} tone="bg-amber-50 border-amber-200 text-amber-700" />
        <Kpi label="Possíveis duplicidades" value={r?.duplicidades} tone="bg-orange-50 border-orange-200 text-orange-600" />
        <Kpi label="Pendentes de processar" value={r?.pendentes} tone="bg-gray-50 border-gray-200 text-gray-600" />
        <Kpi label="Erros" value={r?.erros} tone="bg-red-50 border-red-200 text-red-600" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <i className="ri-list-check-3 text-[#E8610A]"></i>
            <h4 className="text-sm font-bold text-gray-900">Regras manuais</h4>
          </div>
          <button
            onClick={onAplicarRegras}
            disabled={aplicandoRegras}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {aplicandoRegras ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>} Aplicar regras manuais
          </button>
        </div>

        {regras ? (
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
              {regras.aplicadas} aplicadas
            </span>
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              {regras.aguardando} aguardando correspondência
            </span>
            <span className="text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1">
              {regras.total} no total
            </span>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Clique em "Aplicar regras manuais" após a importação.</p>
        )}

        {detalheRegras && detalheRegras.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {detalheRegras.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className={`mt-0.5 w-4 h-4 flex items-center justify-center shrink-0 text-[11px] rounded-full ${
                  d.resultado === 'aplicada' ? 'bg-green-100 text-green-700' : d.resultado === 'ambigua' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  <i className={d.resultado === 'aplicada' ? 'ri-check-line' : d.resultado === 'ambigua' ? 'ri-question-line' : 'ri-subtract-line'}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-gray-800">{d.alvo}
                    <span className="text-xs text-gray-400 ml-2">
                      {d.resultado === 'aplicada' ? 'aplicada' : d.resultado === 'ambigua' ? 'aguardando (ambígua)' : 'não encontrada'}
                    </span>
                  </p>
                  {d.candidatos && d.candidatos.length > 0 && <p className="text-xs text-gray-400">{d.candidatos.join(' · ')}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#E8610A]/5 border border-[#E8610A]/20 rounded-2xl p-4 text-xs text-gray-600 leading-relaxed">
        <p className="font-semibold text-gray-800 mb-1">Sobre a timeline</p>
        Estes arquivos contêm inteligência consolidada e snapshots das últimas mensagens — não a timeline integral das ~575 mil mensagens. As mensagens completas serão importadas depois, a partir do arquivo bruto. A "última interação" mostra o que existe na base histórica disponível.
      </div>
    </div>
  );
}